import { makeStyles } from "@material-ui/core"
import { clsx } from "clsx"
import { FC } from "react"
import { useRecoilValue } from "recoil"
import { colors } from "../../utils/theme"

/**
 * A functional component that renders a statistical tile with customizable text and value.
 * The tile displays a top text, a numeric value (or a placeholder if the value is null),
 * and an optional bottom text. The width of the tile can also be customized.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.topText - The text displayed at the top of the tile.
 * @param {number} [props.value] - The numeric value displayed in the center of the tile. Defaults to "-" if null.
 * @param {string} [props.bottomText] - Optional text displayed at the bottom of the tile.
 * @param {number | string} [props.width] - Optional width of the tile. Accepts either a number or a string.
 * @returns {JSX.Element} A styled tile displaying the provided text and value.
 */
const StatTile: FC<{
  topText: string
  value?: number
  bottomText?: string
  width?: number | string
}> = ({ topText, value, bottomText, width }) => {
  const styles = useStyles()
  return (
    <div className={styles.container} style={{ width: width }}>
      <span className={`${styles.titleText} ${styles.topBottomText}`}>
        {topText}
      </span>
      <span className={styles.value}>
        {value !== null && value !== undefined ? value : "-"}
      </span>
      {bottomText && <span className={styles.topBottomText}>{bottomText}</span>}
    </div>
  )
}

export default StatTile

const useStyles = makeStyles(() => ({
  container: {
    padding: 8,
    backgroundColor: "#FFFFFF", // Replace with your desired background color
    borderWidth: 1,
    borderColor: `${colors.neutral.clay}`,
    gap: 4,
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #C8CDDB",
    borderRadius: 8,
  },
  topBottomText: {
    // color: currentTheme.colors.text.description,
    textAlign: "center",
    color: "#909CBC",
    fontWeight: 400,
    fontSize: 14,
  },
  value: {
    alignSelf: "center",
    color: "#474747",
    fontWeight: 700,
    fontSize: 34,
  },
  titleText: {
    fontWeight: "bold",
  },
}))
